<script setup lang="ts">
import { useStrapi } from '#imports'
import { generateBlogMainPageMeta } from '~/utils/meta/generateBlogPageMeta'

definePageMeta({
  layout: 'marketing'
})

// Define proper interface for article data
interface Article {
  id: number
  title: string
  description: string
  slug: string
  publishedAt: string
  cover?: {
    formats?: {
      medium?: { url: string }
      large?: { url: string }
    }
    alternativeText?: string
  }
  author?: {
    name: string
  }
  category?: {
    name: string
  }
}

const route = useRoute()
const { find } = useStrapi()

// Set meta tags for the blog index page
generateBlogMainPageMeta(route)

const currentPage = ref(1)

// Type assertion for the Strapi response
const { data: articles, refresh } = await useAsyncData('articles', async () => {
  const response = await find('articles', {
    filters: {
      site: { $eq: 'employers.io' }
    },
    populate: {
      cover: true,
      author: true,
      category: true,
      blocks: {
        populate: '*'
      }
    },
    sort: ['publishedAt:desc']
  })
  return response as any // Type assertion to avoid TypeScript errors
})

const handlePageChange = async (page: number) => {
  currentPage.value = page
  await refresh()
}
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="bg-white shadow-sm">
      <div class="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <h1 class="text-4xl font-bold text-gray-900 text-center">
          Career Resources for Job Seekers & Recruiters
        </h1>
        <p class="mt-4 text-xl text-gray-500 text-center max-w-2xl mx-auto">
          Discover the future of work and employment
        </p>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        <!-- Wrap the entire article in NuxtLink -->
        <NuxtLink
          v-for="article in (articles?.data as any[])"
          :key="article.id"
          :to="`/blog/${article.slug}`"
          class="block bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 group"
        >
          <article class="h-full flex flex-col">
            <div class="aspect-video relative overflow-hidden">
              <img
                v-if="article.cover?.formats?.medium?.url"
                :src="article.cover.formats.medium.url"
                :alt="article.cover.alternativeText || article.title"
                width="400"
                height="225"
                class="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              >
              <div
                v-else
                class="w-full h-full bg-gradient-to-r from-blue-500 to-indigo-600"
              />
            </div>

            <div class="p-6 flex-1 flex flex-col">
              <div
                v-if="article.category"
                class="inline-block px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 mb-4"
              >
                {{ article.category.name }}
              </div>

              <h2 class="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                {{ article.title }}
              </h2>

              <p class="text-gray-500 mb-4 line-clamp-3">
                {{ article.description }}
              </p>

              <div class="flex items-center justify-between text-sm text-gray-500 mb-4 mt-auto">
                <div
                  v-if="article.author"
                  class="flex items-center"
                >
                  <span class="font-medium text-gray-900">{{ article.author.name }}</span>
                </div>
                <time class="text-sm text-gray-500">
                  {{ new Date(article.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  }) }}
                </time>
              </div>

              <div
                class="inline-flex items-center text-indigo-600 group-hover:text-indigo-500"
                aria-hidden="true"
              >
                Read more
                <svg
                  class="ml-2 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </div>
            </div>
          </article>
        </NuxtLink>
      </div>
    </div>
  </main>
</template>
